import React from 'react';

import {
  TransactionStatus,
  TransactionType,
  useCreateTransactionMutation,
  useListMarketsQuery,
  useListPortfoliosQuery,
} from '@/generated/graphql';
import { MarketType as Market, PortfolioType as Portfolio } from '@/types';
import { OrderDetails } from '@/components/order-details';
import { Modal } from '@/components/modal';
import { useTransactionForm } from '@/hooks/useTransactionForm';
import { useDialog } from '@/hooks/useDialog';
import { IconPlus } from '@/components/icons';

import TransactionInfo from './components/transaction-info';

const NewTransactionModal: React.FC = () => {
  const { show, handleShow, handleHide, ref } = useDialog();
  const form = useTransactionForm();

  const [portfolio, setPortfolio] = React.useState<Portfolio>();
  const [market, setMarket] = React.useState<Market>();
  const marketsQuery = useListMarketsQuery();
  const portfoliosQuery = useListPortfoliosQuery();
  const [onCreateTransaction] = useCreateTransactionMutation({ refetchQueries: ['listTransactions', 'getPortfolio'] });

  const markets = (marketsQuery?.data?.markets ?? []) as Market[];
  const portfolios = portfoliosQuery?.data?.me?.portfolios;
  const defaultPortfolio = portfolios?.[0];

  React.useEffect(() => {
    if (!portfolio && defaultPortfolio?.name) setPortfolio(defaultPortfolio as Portfolio);
  }, [portfolio, defaultPortfolio]);

  if (markets.length < 1 || portfoliosQuery.loading || portfoliosQuery.error) return null;

  if (form.watch('marketId') && form.watch('marketId') !== market?.id) {
    const selectedMarket = markets.find(({ id }) => id === form.watch('marketId'));
    setMarket(selectedMarket);

    form.setValue('order', { exchangeRate: 1 });
  }

  const onSubmit = async () => {
    const { stockId, portfolioId, order } = form.getValues();
    const input = {
      stockId,
      portfolioId,
      type: TransactionType.Long,
      status: TransactionStatus.Open,
      orders: [order],
    };

    await onCreateTransaction({ variables: { input } });

    handleHide();
  };

  return (
    <>
      <button
        type="button"
        className="relative inline-flex items-center px-4 py-2 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-teal-600 shadow-sm hover:bg-teal-500 focus:outline-none focus:border-teal-700 focus:ring-teal active:bg-teal-700 transition ease-in-out duration-150"
        onClick={handleShow}
      >
        <IconPlus className="-ml-1 mr-2" title="Nueva operación" />
        Nueva operación
      </button>

      {show && (
        <Modal
          isSubmitDisabled={form.formState.isSubmitting || (form.formState.isSubmitted && !form.formState.isValid)}
          onSubmit={form.handleSubmit(onSubmit)}
          onCancel={handleHide}
          ref={ref}
        >
          <TransactionInfo
            portfolios={portfolios as Portfolio[]}
            markets={markets}
            stocks={market?.stocks || []}
            onPortfolioSelect={setPortfolio}
            register={form.register}
            errors={form.errors}
          />
          <OrderDetails market={market} register={form.register} errors={form?.errors?.order} />
        </Modal>
      )}
    </>
  );
};

export default NewTransactionModal;
